// LIGHT COPY of components/topology/TopologyOverview.jsx for /event-trace-light. The dark original is untouched;
// changes to it are NOT carried here automatically.
import React from 'react';
import { KNOWN_DEVICES, PROTOCOL_COLORS, SITE_NATIVE_PREFIX } from '../../constants/topologyLight';
import { deviceTypeLabel, knownDeviceName } from '../../constants/deviceTypes';
import { edgeNodeDisplayName } from '../../utils/edgeNodeDisplayName';
import { computeCounts } from '../../utils/counts';

const MONITORED_PROTOCOLS = ['DNP3', 'MMS', 'GOOSE', 'Modbus', 'SNMP', 'BACnet', 'IT'];

// Tiles that are fully data-driven
function buildTiles(assets, events, edgeNode, windowHours, eventTotal) {
  // Use live assets from API; fall back to KNOWN_DEVICES count for local Pi scenario.
  const assetCount = assets.length;
  // Event counts: utils/counts.js over the same rows and window as the Event Trace header (not a
  // 2000-row "24h" set that never reached 24h). The label states the window the rows cover.
  const counts = computeCounts({ events, hoursBack: windowHours, total: eventTotal });
  const criticalCount = counts.critical;
  const isOnline = edgeNode?.status === 'Online' || edgeNode?.status === 'Active';

  return [
    {
      label: 'Assets Discovered',
      value: assetCount,
      sub: 'registered OT devices',
      color: '#22D3EE',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
        </svg>
      ),
    },
    {
      label: 'Policies Loaded',
      value: edgeNode ? edgeNode.policies_loaded : '—',
      sub: `${edgeNode?.policies_enabled || 0} enabled`,
      color: '#10B981',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
    },
    {
      label: 'Critical Events',
      value: criticalCount,
      // Capped: the alerts are over the latest rows, not the total the label also shows (R-500).
      sub: `of ${counts.alerts} alerts${counts.window.capped ? ` in latest ${counts.window.limit}` : ''} · ${counts.window.label}`,
      color: criticalCount > 0 ? '#EF4444' : '#10B981',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
    },
    {
      label: 'Protocols Monitored',
      value: MONITORED_PROTOCOLS.length,
      sub: 'industrial + IT',
      color: '#F97316',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
    },
    {
      label: 'WAN Connection',
      value: isOnline ? 'Connected' : (edgeNode ? 'Offline' : '—'),
      sub: edgeNode ? edgeNodeDisplayName(edgeNode, 'edge node') : 'edge node',
      color: isOnline ? '#10B981' : '#EF4444',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0" />
        </svg>
      ),
    },
    {
      label: 'CISA KEV ICS/OT',
      value: edgeNode?.cisa_kev_count != null ? edgeNode.cisa_kev_count : '174',
      sub: edgeNode?.cisa_kev_count != null ? 'live from agent' : 'as of last sync',
      color: '#8B5CF6',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4" />
        </svg>
      ),
    },
    {
      label: 'OTX Indicators',
      value: edgeNode?.otx_indicator_count != null
        ? (edgeNode.otx_indicator_count >= 1000
            ? `${Math.round(edgeNode.otx_indicator_count / 1000)}K`
            : edgeNode.otx_indicator_count)
        : '453K',
      sub: edgeNode?.otx_indicator_count != null ? 'live from agent' : 'as of last sync',
      color: '#EC4899',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
    },
    {
      // The logged events this page loaded (capped by the request limit) — not sealed compliance evidence.
      label: 'Event capture',
      value: events.length > 0 ? 'Loaded' : 'None',
      // Capped: the label already states "latest 500 of N events" (R-500); not capped: "N events · …".
      sub: counts.window.capped ? counts.window.label : `${counts.eventsText} · ${counts.window.label}`,
      color: events.length > 0 ? '#10B981' : '#64748B',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
  ];
}

export default function TopologyOverview({ assets, events, edgeNode, windowHours = 2, eventTotal = null }) {
  const nativePrefix = edgeNode ? (SITE_NATIVE_PREFIX[edgeNode.edge_node_id] || null) : null;
  const kdFallback = (prefix) => Object.entries(KNOWN_DEVICES)
    .filter(([ip]) => !prefix || ip.startsWith(prefix))
    .map(([ip, kd]) => ({
      ip_address: ip, asset_name: knownDeviceName(ip, kd.name), asset_type: kd.role, vendor: kd.vendor,
      risk_level: null, event_count_24h: 0, enforcement_state: null,
    }));
  const rosterAssets = assets.length > 0 ? assets : kdFallback(nativePrefix);
  const tiles = buildTiles(rosterAssets, events, edgeNode, windowHours, eventTotal);
  const isOnline = edgeNode?.status === 'Online' || edgeNode?.status === 'Active';

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-6 max-w-5xl mx-auto space-y-8">

        {/* Architecture diagram */}
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4">
            System Architecture
          </div>
          <div className="relative">
            {/* Cloud tier */}
            <div className="flex justify-center mb-2">
              <div className="px-8 py-3 rounded-xl bg-slate-100/60 border border-sky-400"
                style={{ boxShadow: '0 0 24px rgba(56,189,248,0.08)' }}>
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-sky-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                  </svg>
                  <div>
                    <div className="text-sm font-semibold text-sky-700">EdgeNotary Cloud</div>
                    <div className="text-[11px] text-slate-600">Management · Policy Engine · Intelligence</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center">
              <div className="w-px h-6 bg-sky-100" />
            </div>
            <div className="flex justify-center mb-1">
              <span className="text-[10px] text-sky-700 bg-white px-1">secure mgmt tunnel</span>
            </div>
            <div className="flex justify-center mb-2">
              <div className="w-px h-4 bg-sky-100" />
            </div>

            {/* Edge node tier */}
            <div className="flex justify-center mb-2">
              <div className="px-8 py-3 rounded-xl bg-slate-100/60 border border-emerald-400"
                style={{ boxShadow: '0 0 20px rgba(16,185,129,0.06)' }}>
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                  <div>
                    <div className="text-sm font-semibold text-emerald-700">
                      Celerway Edge Agent · {edgeNode ? edgeNodeDisplayName(edgeNode, 'X2mNative') : 'X2mNative'}
                    </div>
                    <div className="text-[11px] text-slate-600">
                      DPI · OT Protocol Analysis · CISA KEV · OTX · Policy Monitoring
                    </div>
                  </div>
                  <div className={`ml-4 text-[10px] px-2 py-0.5 rounded border ${isOnline ? 'text-emerald-700 border-emerald-300 bg-emerald-50' : 'text-slate-600 border-slate-200'}`}>
                    {edgeNode?.status || 'Offline'}
                  </div>
                </div>
              </div>
            </div>

            {/* Connector */}
            <div className="flex justify-center">
              <div className="w-px h-6 bg-emerald-100" />
            </div>
            <div className="flex justify-center mb-1">
              <span className="text-[10px] text-emerald-800 bg-white px-1">passive DPI / tap</span>
            </div>
            <div className="flex justify-center mb-2">
              <div className="w-px h-4 bg-emerald-100" />
            </div>

            {/* OT Zone tier */}
            <div className="flex justify-center">
              <div className="px-8 py-3 rounded-xl bg-slate-100/60 border border-cyan-400" style={{ minWidth: 400 }}>
                <div className="text-center mb-2">
                  <span className="text-xs font-semibold text-cyan-700">Monitored OT Zone</span>
                  <span className="ml-2 text-[11px] text-slate-600">{rosterAssets.length} assets · {MONITORED_PROTOCOLS.length} protocols</span>
                </div>
                <div className="flex flex-wrap justify-center gap-2">
                  {MONITORED_PROTOCOLS.map(p => {
                    const pKey = p.toLowerCase();
                    const color = PROTOCOL_COLORS[pKey] || '#475569';
                    return (
                      <span key={p} className="text-[11px] px-2 py-0.5 rounded font-semibold"
                        style={{ background: `${color}1A`, color, border: `1px solid ${color}40` }}>
                        {p} ✓
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Status tiles */}
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-4">
            Operational Status
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {tiles.map(tile => (
              <div key={tile.label}
                className="p-4 rounded-xl bg-slate-100/60 border border-slate-200 hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between mb-2">
                  <div style={{ color: tile.color }} className="opacity-80">{tile.icon}</div>
                </div>
                <div className="text-xl font-bold mb-0.5" style={{ color: tile.color }}>{tile.value}</div>
                <div className="text-[11px] font-semibold text-slate-700 leading-tight">{tile.label}</div>
                <div className="text-[10px] text-slate-500 mt-0.5">{tile.sub}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Asset roster */}
        <div>
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-3">
            Asset Roster
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[...rosterAssets]
              .sort((a, b) => {
                if (a.enforcement_state === 'enforcement' && b.enforcement_state !== 'enforcement') return -1;
                if (b.enforcement_state === 'enforcement' && a.enforcement_state !== 'enforcement') return 1;
                const tier = r => r?.risk_level === 'critical' ? 0 : r?.risk_level === 'high' ? 1 : 2;
                if (tier(a) !== tier(b)) return tier(a) - tier(b);
                return (b.event_count_24h || 0) - (a.event_count_24h || 0);
              })
              .map(asset => {
                const ip = asset.ip_address;
                const kd = KNOWN_DEVICES[ip] || {};
                const name   = asset.asset_name || knownDeviceName(ip, kd.name)  || ip;
                const vendor = asset.vendor      || kd.vendor || '';
                const role   = deviceTypeLabel(name, vendor, kd.role || asset.asset_type || 'Device');
                const riskColor = asset.enforcement_state === 'enforcement' ? '#F97316' :
                                  asset.risk_level === 'critical' ? '#EF4444' :
                                  asset.risk_level === 'high' ? '#F97316' : '#10B981';
                return (
                  <div key={ip} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-100/40 border border-slate-200">
                    <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: riskColor }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-slate-800 truncate">{name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{ip} · {role} · {vendor}</div>
                    </div>
                    {asset.enforcement_state === 'enforcement' && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-300 flex-shrink-0">BLOCKED</span>
                    )}
                    {asset.event_count_24h > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 flex-shrink-0">
                        {asset.event_count_24h}
                      </span>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

      </div>
    </div>
  );
}
